/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { collection, addDoc, doc, updateDoc, getDoc, getDocs, query, where } from "firebase/firestore"
import { db } from "../../firebase"
import { toast } from 'react-toastify'
import dayjs from "dayjs"
import { tokens } from "../../theme"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { IBusinessHoliday, isDateInHolidays } from "../../utils/businessHolidays"

// 管理項目の定義
const CHECK_CATEGORIES = [
  {
    key: "facilityChecks",
    title: "施設設備の清掃状態",
    rooms: ["解体処理室", "冷蔵庫", "カット室", "製品保管庫"],
    items: [
      "食品や廃棄物の残さが残ってないか",
      "水切りは良好か",
      "不要な物はないか、整理整頓されているか",
      "カビ、サビやこびりついた汚れや血液はないか",
      "洗浄・消毒は実施したか ※必要箇所のみ",
      "洗浄剤、消毒剤は残っていないか",
      "洗浄･消毒設備は使用可能で、器具等の消毒に使用する温湯又は蒸気の温度は適切か",
      "破損、故障、⻲裂はないか",
      "荷受場及びその周辺の地面は清掃されているか"
    ]
  },
  {
    key: "handwashChecks",
    title: "手洗い設備の清掃状態",
    rooms: ["解体処理室", "冷蔵庫", "カット室"],
    items: [
      "汚れが残っていないか",
      "洗浄剤、消毒剤、ペーパータオルが不足していないか",
      "破損、故障はなく使用可能か"
    ]
  },
  {
    key: "toiletChecks",
    title: "トイレの清掃状態",
    rooms: ["トイレ"],
    items: [
      "汚れが残っていないか",
      "洗浄剤、消毒剤、ペーパータオルが不足してないか"
    ]
  },
  {
    key: "equipmentChecks",
    title: "器具類の清掃状態",
    rooms: ["解体処理室", "冷蔵庫", "カット室", "製品保管庫"],
    items: [
      "食品や廃棄物の残さが残ってないか",
      "水切りは良好か",
      "カビ、サビやこびりついた汚れや血液はないか",
      "洗浄・消毒は実施したか ※必要箇所のみ",
      "洗浄剤、消毒剤は残っていないか",
      "破損した器具がないか",
      "機械に欠落した部品はないか"
    ]
  },
  {
    key: "pestChecks",
    title: "そ族昆虫に関すること",
    rooms: ["施設全般"],
    items: ["ねずみや昆虫が発生していないか"]
  },
  {
    key: "waterChecks",
    title: "使用水に関すること",
    rooms: ["事務室"],
    items: [
      "「味、色、臭い、濁り」に異常はないか",
      "残留塩素は0.1ｐｐｍ以上か"
    ]
  },
  {
    key: "wasteChecks",
    title: "廃棄物に関すること",
    rooms: ["ゴミ箱がある場所、廃棄物集積場所"],
    items: [
      "廃棄物は全て排出されているか",
      "汚物の残存はないか",
      "悪臭はないか",
      "ねずみや昆虫が発生していないか",
      "破損はないか"
    ]
  }
]

interface DailyHygieneData {
  id?: string
  year: string
  month: string
  day: string
  date: string
  isHoliday?: boolean
  manager: string
  facilityChecks: Record<string, string>
  handwashChecks: Record<string, string>
  toiletChecks: Record<string, string>
  equipmentChecks: Record<string, string>
  pestChecks: Record<string, string>
  waterChecks: Record<string, string>
  wasteChecks: Record<string, string>
  verifier: string
  remarks: string
  createdAt?: string
  updatedAt?: string
}

const DailyHygieneManagementForm = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isEditMode = !!(id && id !== 'new')

  const [processing, setProcessing] = useState<boolean>(false)
  const [targetDate, setTargetDate] = useState<string>("")
  const [year, setYear] = useState<string>("")
  const [month, setMonth] = useState<string>("")
  const [day, setDay] = useState<string>("")
  const [manager, setManager] = useState<string>("")
  const [facilityChecks, setFacilityChecks] = useState<Record<string, string>>({})
  const [handwashChecks, setHandwashChecks] = useState<Record<string, string>>({})
  const [toiletChecks, setToiletChecks] = useState<Record<string, string>>({})
  const [equipmentChecks, setEquipmentChecks] = useState<Record<string, string>>({})
  const [pestChecks, setPestChecks] = useState<Record<string, string>>({})
  const [waterChecks, setWaterChecks] = useState<Record<string, string>>({})
  const [wasteChecks, setWasteChecks] = useState<Record<string, string>>({})
  const [verifier, setVerifier] = useState<string>("")
  const [remarks, setRemarks] = useState<string>("")

  useEffect(() => {
    if (!isEditMode) {
      // New record: get date from URL parameter
      const dateParam = searchParams.get('date')
      let targetYear = ""
      let targetMonth = ""
      let targetDay = ""

      if (dateParam) {
        const date = dayjs(dateParam)
        setTargetDate(dateParam)
        targetYear = date.format("YYYY")
        targetMonth = date.format("MM")
        targetDay = date.format("D")
        setYear(targetYear)
        setMonth(targetMonth)
        setDay(targetDay)
      } else {
        const today = dayjs()
        setTargetDate(today.format("YYYY-MM-DD"))
        targetYear = today.format("YYYY")
        targetMonth = today.format("MM")
        targetDay = today.format("D")
        setYear(targetYear)
        setMonth(targetMonth)
        setDay(targetDay)
      }

      // Check if record already exists for this date
      checkExistingRecord(targetYear, targetMonth, targetDay)
    } else {
      loadRecord(id)
    }
  }, [id, isEditMode, searchParams])

  const initializeChecks = () => {
    const initState: Record<string, string> = {}
    CHECK_CATEGORIES.forEach(category => {
      category.items.forEach((_, index) => {
        initState[`item${index}`] = ""
      })
    })

    setFacilityChecks({ ...initState })
    setHandwashChecks({ ...initState })
    setToiletChecks({ ...initState })
    setEquipmentChecks({ ...initState })
    setPestChecks({ ...initState })
    setWaterChecks({ ...initState })
    setWasteChecks({ ...initState })
  }

  const checkExistingRecord = async (targetYear: string, targetMonth: string, targetDay: string) => {
    setProcessing(true)
    try {
      const q = query(
        collection(db, "dailyHygieneManagements"),
        where("year", "==", targetYear),
        where("month", "==", targetMonth),
        where("day", "==", targetDay)
      )
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        // Record exists, load it as edit mode
        const existingDoc = querySnapshot.docs[0]
        const record = {
          id: existingDoc.id,
          ...existingDoc.data()
        } as DailyHygieneData

        // Check if it's a holiday record
        if (record.isHoliday === true) {
          toast.info("この日は休業日として登録されています。一覧画面に戻ります。")
          navigate('/admin/daily-hygiene-management', { replace: true })
          return
        }

        toast.info("既に入力済みのデータがあります。編集モードで開きます。")

        setManager(record.manager)
        setFacilityChecks(record.facilityChecks || {})
        setHandwashChecks(record.handwashChecks || {})
        setToiletChecks(record.toiletChecks || {})
        setEquipmentChecks(record.equipmentChecks || {})
        setPestChecks(record.pestChecks || {})
        setWaterChecks(record.waterChecks || {})
        setWasteChecks(record.wasteChecks || {})
        setVerifier(record.verifier || "")
        setRemarks(record.remarks || "")

        // Update URL to edit mode without page reload
        navigate(`/admin/daily-hygiene-management/${existingDoc.id}`, { replace: true })
      } else {
        // 休業日マスタに登録されている日付かどうかを確認
        const dateStr = `${targetYear}-${targetMonth}-${targetDay.padStart(2, '0')}`
        const holidaysSnapshot = await getDocs(collection(db, "businessHolidays"))
        const holidays = holidaysSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday))

        if (isDateInHolidays(dateStr, holidays)) {
          toast.info("この日は休業日として登録されています。一覧画面に戻ります。")
          navigate('/admin/daily-hygiene-management', { replace: true })
          return
        }

        // No existing record, initialize as new
        initializeChecks()
      }
    } catch (e) {
      console.error("Error checking existing record:", e)
      toast.error("データの確認に失敗しました")
      initializeChecks()
    } finally {
      setProcessing(false)
    }
  }

  const loadRecord = async (recordId: string) => {
    setProcessing(true)
    try {
      const docRef = doc(db, "dailyHygieneManagements", recordId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const record = {
          id: docSnap.id,
          ...docSnap.data()
        } as DailyHygieneData

        setTargetDate(record.date)
        setYear(record.year)
        setMonth(record.month)
        setDay(record.day)
        setManager(record.manager)
        setFacilityChecks(record.facilityChecks || {})
        setHandwashChecks(record.handwashChecks || {})
        setToiletChecks(record.toiletChecks || {})
        setEquipmentChecks(record.equipmentChecks || {})
        setPestChecks(record.pestChecks || {})
        setWaterChecks(record.waterChecks || {})
        setWasteChecks(record.wasteChecks || {})
        setVerifier(record.verifier || "")
        setRemarks(record.remarks || "")
      } else {
        toast.error("レコードが見つかりません")
        navigate('/admin/daily-hygiene-management')
      }
    } catch (e) {
      toast.error("データの読み込みに失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const getCheckState = (category: string): [Record<string, string>, React.Dispatch<React.SetStateAction<Record<string, string>>>] => {
    switch (category) {
      case "facilityChecks": return [facilityChecks, setFacilityChecks]
      case "handwashChecks": return [handwashChecks, setHandwashChecks]
      case "toiletChecks": return [toiletChecks, setToiletChecks]
      case "equipmentChecks": return [equipmentChecks, setEquipmentChecks]
      case "pestChecks": return [pestChecks, setPestChecks]
      case "waterChecks": return [waterChecks, setWaterChecks]
      case "wasteChecks": return [wasteChecks, setWasteChecks]
      default: return [{}, () => {}]
    }
  }

  const handleCheckChange = (category: string, itemKey: string, value: string) => {
    const [state, setState] = getCheckState(category)
    setState({
      ...state,
      [itemKey]: value
    })
  }

  const saveData = async () => {
    if (!manager) {
      toast.error("衛生管理者担当責任者を入力してください")
      return
    }

    if (!verifier) {
      toast.error("確認者を入力してください")
      return
    }

    setProcessing(true)
    const data = {
      year,
      month,
      day,
      date: targetDate,
      manager,
      facilityChecks,
      handwashChecks,
      toiletChecks,
      equipmentChecks,
      pestChecks,
      waterChecks,
      wasteChecks,
      verifier,
      remarks,
      updatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
    }

    try {
      if (isEditMode) {
        await updateDoc(doc(db, "dailyHygieneManagements", id), data)
        toast.success("衛生管理点検票を更新しました")
      } else {
        // Check if record already exists for this date
        const q = query(
          collection(db, "dailyHygieneManagements"),
          where("year", "==", year),
          where("month", "==", month),
          where("day", "==", day)
        )
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          toast.error("この日付のデータは既に存在します")
          setProcessing(false)
          return
        }

        const newData = {
          ...data,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
        }
        await addDoc(collection(db, "dailyHygieneManagements"), newData)
        toast.success("衛生管理点検票を保存しました")
      }
      navigate('/admin/daily-hygiene-management')
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleBack = () => {
    navigate('/admin/daily-hygiene-management')
  }

  return (
    <Box m={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEditMode ? '衛生管理点検票(日次) 編集' : '衛生管理点検票(日次) 新規作成'}
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          基本情報
        </Typography>

        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
          <TextField
            label="対象日"
            type="date"
            value={targetDate}
            disabled
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="衛生管理者担当責任者"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            fullWidth
            required
          />
        </Box>

        <Typography variant="body2" color="textSecondary" mb={3}>
          始業開始前に実施すること。適切に実施されている項目にチェックを入れてください。チェックが入っていない項目は不適（×）として記録されます。不適（×）の場合は、措置結果を特記事項に記載すること。
        </Typography>
      </Paper>

      {CHECK_CATEGORIES.map((category) => {
        const [checkState] = getCheckState(category.key)

        return (
          <Paper key={category.key} elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ backgroundColor: colors.primary[400], p: 1 }}>
              {category.title}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              確認する部屋等: {category.rooms.join("、")}
            </Typography>
            <Typography variant="body2" fontWeight="bold" mb={2}>
              管理事項:
            </Typography>

            {category.items.map((item, index) => {
              const itemKey = `item${index}`
              const value = (checkState as Record<string, string>)[itemKey] || ""

              return (
                <Box key={index} mb={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value === "○"}
                        onChange={(e) => handleCheckChange(category.key, itemKey, e.target.checked ? "○" : "×")}
                        color="success"
                      />
                    }
                    label={`${index + 1}. ${item}`}
                    sx={{ flex: 1 }}
                  />
                </Box>
              )
            })}
          </Paper>
        )
      })}

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2} sx={{ backgroundColor: colors.primary[400], p: 1 }}>
          確認者
        </Typography>
        <TextField
          value={verifier}
          onChange={(e) => setVerifier(e.target.value)}
          fullWidth
          placeholder="確認者名を入力"
          required
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" fontWeight="bold" mb={2}>
          特記事項
        </Typography>
        <TextField
          multiline
          rows={6}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          placeholder="×の場合は、措置結果をここに記載すること"
        />

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={handleBack}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={saveData}
            disabled={processing}
          >
            {isEditMode ? "更新" : "保存"}
          </Button>
        </Box>
      </Paper>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={processing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default DailyHygieneManagementForm
